import { useEffect, useState } from "react";
import useSsoService from "../../hooks/useSsoService";
import { CurrencyContext } from "./CurrencyContext";
import { Currency } from "../../types/Currency/Currency";
import { useAuthContext } from "../../components/auth/AuthContext";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { CurrentUser } from "../../types/User";
import { LocalStorageKeys } from "../../types/LocalStorageKeys";
import { useTenantContext } from "../Tenant/TenantContext";

export interface CurrencyProps {
  children: React.ReactNode;
}

interface CustomJwtPayload extends JwtPayload {
  OrganisationTransactionCurrency: string; // Adjust the type based on your actual data
}

const CurrencyProvider = ({ children }: CurrencyProps) => {
  const { config } = useTenantContext();

  const getCurrency = (user: CurrentUser | undefined) => {
    let transactionCurrency: string | null = null;

    if (user) {
      const autoDetected = localStorage.getItem(LocalStorageKeys.AUTO_DETECTED_CURRENCY);
      const localStorageCurrency = localStorage.getItem(LocalStorageKeys.TRANSACTION_CURRENCY);

      if (localStorageCurrency && !autoDetected) {
        transactionCurrency = localStorageCurrency;
      } else {
        transactionCurrency = (jwtDecode(user.token) as CustomJwtPayload)
          .OrganisationTransactionCurrency;
        localStorage.removeItem(LocalStorageKeys.AUTO_DETECTED_CURRENCY);
      }
    } else {
      const manualSelection = localStorage.getItem(
        LocalStorageKeys.TRANSACTION_CURRENCY
      );

      if (manualSelection) {
        transactionCurrency = manualSelection;
      } else {
        transactionCurrency = config.defaultCurrency ?? "GBP";
      }
    }

    if (transactionCurrency && currencies && Array.isArray(currencies)) {
      const currency = currencies.find((c) => c.code === transactionCurrency);
      return currency;
    }

    return undefined;
  };

  const ssoService = useSsoService();
  const [currencies, setCurrencies] = useState<Currency[] | null>([]);
  const { user } = useAuthContext();

  const [selectedCurrency, setSelectedCurrency] = useState<
    Currency | undefined
  >(undefined);

  useEffect(() => {
    const storedCurrencies = localStorage.getItem(LocalStorageKeys.CURRENCIES);
    if (storedCurrencies) {
      setCurrencies(JSON.parse(storedCurrencies));
    } else {
      ssoService
        .GetCurrencies()
        .then((data) => {
          setCurrencies(data);
          localStorage.setItem(
            LocalStorageKeys.CURRENCIES,
            JSON.stringify(data)
          );
        })
        .catch(() => {});
    }
  }, [ssoService, setCurrencies]);

  useEffect(() => {
    if (!user && !localStorage.getItem(LocalStorageKeys.TRANSACTION_CURRENCY)) {
      localStorage.setItem(LocalStorageKeys.AUTO_DETECTED_CURRENCY, "true");
    }
    setSelectedCurrency(getCurrency(user));
  }, [user, currencies]);

  useEffect(() => {
    if (selectedCurrency) {
      localStorage.setItem(
        LocalStorageKeys.TRANSACTION_CURRENCY,
        selectedCurrency.code
      );
    }
  }, [selectedCurrency]);

  return (
    <CurrencyContext.Provider
      value={{ currencies, selectedCurrency, setSelectedCurrency }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;
